import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  Pagination,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tabs,
  Tab,
  Box as MuiBox
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { syncDrill4JTests, getDrill4JTests, getDrill4JTestsByDiscrepancyType, getTestsNotInDrill4J } from '../../services/apiService';

/**
 * Drill4J Tests Tab component
 * Displays automated tests and their registration status in Drill4J
 */
const Drill4JTestsTab = () => {
  const [tests, setTests] = useState([]);
  const [statistics, setStatistics] = useState({ total: 0, registered: 0, notRegistered: 0 });
  const [drill4jStatistics, setDrill4jStatistics] = useState(null);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncComplete, setSyncComplete] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalTests, setModalTests] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Not in Drill4J modal state
  const [notInDrill4JModalOpen, setNotInDrill4JModalOpen] = useState(false);
  const [notInDrill4JData, setNotInDrill4JData] = useState(null);
  const [notInDrill4JLoading, setNotInDrill4JLoading] = useState(false);
  const [notInDrill4JTab, setNotInDrill4JTab] = useState(0); // 0 = testSuite, 1 = automationStatus

  // Filters
  const [adoIdFilter, setAdoIdFilter] = useState('');
  const [componentFilter, setComponentFilter] = useState('');
  const [subComponentFilter, setSubComponentFilter] = useState('');
  const [registeredFilter, setRegisteredFilter] = useState('');
  const [suiteFilter, setSuiteFilter] = useState('');

  // Sorting
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');

  // Pagination
  const [page, setPage] = useState(1);
  const pageSize = 200;

  // Get distinct components, subComponents, and suites from tests
  const components = [...new Set(tests.map(t => t.component).filter(Boolean))].sort();
  const subComponents = componentFilter
    ? [...new Set(tests.filter(t => t.component === componentFilter).map(t => t.subComponent).filter(Boolean))].sort()
    : [...new Set(tests.map(t => t.subComponent).filter(Boolean))].sort();
  const suites = [...new Set(tests.map(t => t.suite).filter(Boolean))].sort();

  // Fetch tests data
  const fetchTests = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters = {};
      if (adoIdFilter) filters.adoId = adoIdFilter;
      if (componentFilter) filters.component = componentFilter;
      if (subComponentFilter) filters.subComponent = subComponentFilter;
      if (registeredFilter !== '') filters.registered = registeredFilter;
      if (suiteFilter) filters.suite = suiteFilter;

      const response = await getDrill4JTests(filters);

      if (response.success && response.data) {
        setTests(response.data.tests || []);
        setStatistics(response.data.statistics || { total: 0, registered: 0, notRegistered: 0 });
        setDrill4jStatistics(response.data.drill4jStatistics || null);
        setLastSyncedAt(response.data.lastSyncedAt || null);
      } else {
        throw new Error(response.message || 'Failed to fetch tests');
      }
    } catch (err) {
      console.error('Error fetching Drill4J tests:', err);
      setError(err.message || 'Failed to load tests');
    } finally {
      setLoading(false);
    }
  };

  // Handle sync button click
  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncComplete(false);
      setError(null);
      setSuccessMessage(null);

      const response = await syncDrill4JTests();

      if (response.success) {
        setSyncComplete(true);
        const matchInfo = response.matchedCount 
          ? ` ${response.matchedCount} matched.`
          : '';
        setSuccessMessage(
          `Successfully synced ${response.syncedCount} test definitions. ` +
          `${response.createdCount} created, ${response.updatedCount} updated.${matchInfo}`
        );
        setLastSyncedAt(response.syncedAt || new Date());
        
        // Refresh tests data after sync to update counts
        await fetchTests();
        
        // Keep sync complete indicator for a few seconds
        setTimeout(() => {
          setSyncComplete(false);
        }, 3000);
      } else {
        throw new Error(response.message || 'Sync failed');
      }
    } catch (err) {
      console.error('Error syncing Drill4J tests:', err);
      setError(err.message || 'Failed to sync tests');
      setSyncComplete(false);
    } finally {
      setSyncing(false);
    }
  };

  // Handle filter changes
  const handleAdoIdChange = (event) => {
    setAdoIdFilter(event.target.value);
  };

  const handleComponentChange = (event) => {
    setComponentFilter(event.target.value);
    setSubComponentFilter(''); // Reset subComponent when component changes
  };

  const handleSubComponentChange = (event) => {
    setSubComponentFilter(event.target.value);
  };

  const handleRegisteredChange = (event) => {
    setRegisteredFilter(event.target.value);
  };

  const handleSuiteChange = (event) => {
    setSuiteFilter(event.target.value);
  };

  // Handle sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Sorting comparator
  const descendingComparator = (a, b, orderBy) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];

    // Handle null/undefined values
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    // Handle dates
    if (orderBy === 'executedOn') {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
    }

    // Handle booleans
    if (typeof aValue === 'boolean') {
      aValue = aValue ? 1 : 0;
      bValue = bValue ? 1 : 0;
    }

    // Handle strings (case-insensitive)
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (bValue < aValue) {
      return -1;
    }
    if (bValue > aValue) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  // Sort tests with useMemo for performance
  const sortedTests = useMemo(() => {
    return [...tests].sort(getComparator(order, orderBy));
  }, [tests, order, orderBy]);

  // Pagination calculations
  const totalPages = Math.ceil(sortedTests.length / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTests = useMemo(() => {
    return sortedTests.slice(startIndex, endIndex);
  }, [sortedTests, startIndex, endIndex]);

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
    // Scroll to top of table when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [adoIdFilter, componentFilter, subComponentFilter, registeredFilter, suiteFilter]);

  // Apply filters when they change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchTests();
    }, 500); // Debounce filter changes

    return () => clearTimeout(timeoutId);
  }, [adoIdFilter, componentFilter, subComponentFilter, registeredFilter, suiteFilter]);

  // Initial fetch
  useEffect(() => {
    fetchTests();
  }, []);

  // Close messages
  const handleCloseError = () => {
    setError(null);
  };

  const handleCloseSuccess = () => {
    setSuccessMessage(null);
  };

  // Handle discrepancy number click
  const handleDiscrepancyClick = async (type) => {
    setModalType(type);
    setModalOpen(true);
    setModalLoading(true);
    setModalTests([]);

    try {
      const response = await getDrill4JTestsByDiscrepancyType(type);
      if (response.success && response.data) {
        setModalTests(response.data.tests || []);
      } else {
        throw new Error(response.message || 'Failed to fetch tests');
      }
    } catch (err) {
      console.error(`Error fetching ${type} tests:`, err);
      setError(err.message || `Failed to load ${type} tests`);
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalType(null);
    setModalTests([]);
  };

  const getModalTitle = () => {
    const titles = {
      unmatched: 'Discrepancy Tests - Unmatched',
      deleted: 'Discrepancy Tests - Deleted',
      other: 'Discrepancy Tests - Other'
    };
    return titles[modalType] || 'Discrepancy Tests';
  };

  // Handle Not in Drill4J click
  const handleNotInDrill4JClick = async () => {
    setNotInDrill4JModalOpen(true);
    setNotInDrill4JLoading(true);
    setNotInDrill4JData(null);
    setNotInDrill4JTab(0);

    try {
      const response = await getTestsNotInDrill4J();
      if (response.success && response.data) {
        setNotInDrill4JData(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch tests');
      }
    } catch (err) {
      console.error('Error fetching tests not in Drill4J:', err);
      setError(err.message || 'Failed to load tests');
    } finally {
      setNotInDrill4JLoading(false);
    }
  };

  const handleCloseNotInDrill4JModal = () => {
    setNotInDrill4JModalOpen(false);
    setNotInDrill4JData(null);
    setNotInDrill4JTab(0);
  };

  return (
    <Box>
      {/* Sync Progress Indicator */}
      {syncing && (
        <Alert 
          severity="info" 
          icon={<CircularProgress size={20} />}
          sx={{ mb: 2 }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Syncing from Drill4J and matching tests...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This may take a few moments. Please wait...
          </Typography>
        </Alert>
      )}
      
      {/* Sync Complete Indicator */}
      {syncComplete && !syncing && (
        <Alert 
          severity="success" 
          icon={<CheckCircleIcon />}
          sx={{ mb: 2 }}
          onClose={() => setSyncComplete(false)}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Sync Complete!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            All test definitions have been synced and matched. Statistics have been updated.
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          Drill4J Tests Registration Status
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {lastSyncedAt && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Last synced: {new Date(lastSyncedAt).toLocaleString()}
            </Typography>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={syncing ? <CircularProgress size={20} /> : <RefreshIcon />}
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? 'Syncing...' : 'Sync from Drill4J DB'}
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards - 5 cards in one row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Card 1: Tests in ADO (Automated) */}
        <Grid item xs={12} sm={6} md={true}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              color: '#1565c0',
              minHeight: 120,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, fontWeight: 500 }}>
              Tests in ADO (Automated)
            </Typography>
            {syncing || loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 40 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {statistics.total}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Card 2: Executed in Drill4J */}
        <Grid item xs={12} sm={6} md={true}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)',
              color: '#c2185b',
              minHeight: 120,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, fontWeight: 500 }}>
              Executed in Drill4J
            </Typography>
            {syncing || loading || !drill4jStatistics ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 40 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {drill4jStatistics.totalInDrill4JDB}
                </Typography>
                {drill4jStatistics.isFiltered && (
                  <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.7, fontSize: '0.65rem', fontStyle: 'italic' }}>
                    (filtered)
                  </Typography>
                )}
              </>
            )}
          </Paper>
        </Grid>

        {/* Card 3: Registered in Drill4J from ADO tests */}
        <Grid item xs={12} sm={6} md={true}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
              color: '#2e7d32',
              minHeight: 120,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, fontWeight: 500 }}>
              Registered in Drill4J from ADO tests
            </Typography>
            {syncing || loading || !drill4jStatistics ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 40 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {drill4jStatistics.registeredInUI}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Card 4: Discrepancy - Clickable */}
        <Grid item xs={12} sm={6} md={true}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
              color: '#e65100',
              minHeight: 120,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              cursor: drill4jStatistics && drill4jStatistics.discrepancy > 0 ? 'pointer' : 'default',
              transition: 'transform 0.2s, box-shadow 0.2s',
              ...(drill4jStatistics && drill4jStatistics.discrepancy > 0 && {
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              })
            }}
            onClick={() => {
              if (drill4jStatistics && drill4jStatistics.discrepancy > 0) {
                // Show unmatched tests (main discrepancy type)
                handleDiscrepancyClick('unmatched');
              }
            }}
          >
            <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, fontWeight: 500 }}>
              Discrepancy
            </Typography>
            {syncing || loading || !drill4jStatistics ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 40 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {drill4jStatistics.discrepancy}
                </Typography>
                {drill4jStatistics.isFiltered && (
                  <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.7, fontSize: '0.65rem', fontStyle: 'italic' }}>
                    (not applicable with filters)
                  </Typography>
                )}
              </>
            )}
            {drill4jStatistics && drill4jStatistics.discrepancy > 0 && !drill4jStatistics.isFiltered && (
              <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.8, fontSize: '0.7rem' }}>
                Click to view details
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Card 5: Not in Drill4J */}
        <Grid item xs={12} sm={6} md={true}>
          <Paper 
            elevation={2} 
            sx={{ 
              p: 2, 
              textAlign: 'center', 
              background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
              color: '#7b1fa2',
              minHeight: 120,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              cursor: (statistics.total - (drill4jStatistics?.registeredInUI || 0)) > 0 ? 'pointer' : 'default',
              transition: 'transform 0.2s, box-shadow 0.2s',
              ...((statistics.total - (drill4jStatistics?.registeredInUI || 0)) > 0 && {
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              })
            }}
            onClick={() => {
              const notInDrill4JCount = statistics.total - (drill4jStatistics?.registeredInUI || 0);
              if (notInDrill4JCount > 0) {
                handleNotInDrill4JClick();
              }
            }}
          >
            <Typography variant="body2" sx={{ mb: 1, opacity: 0.9, fontWeight: 500 }}>
              Not in Drill4J
            </Typography>
            {syncing || loading || !drill4jStatistics ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 40 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {statistics.total - (drill4jStatistics?.registeredInUI || 0)}
              </Typography>
            )}
            {(statistics.total - (drill4jStatistics?.registeredInUI || 0)) > 0 && (
              <Typography variant="caption" sx={{ mt: 0.5, opacity: 0.8, fontSize: '0.7rem' }}>
                Click to view breakdown
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>


      {/* Search Filters */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Search Filters
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 auto' }, minWidth: { md: 0 } }}>
            <TextField
              fullWidth
              label="ADO ID"
              value={adoIdFilter}
              onChange={handleAdoIdChange}
              placeholder="Enter ADO ID"
              size="small"
            />
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 auto' }, minWidth: { md: 0 } }}>
            <TextField
              fullWidth
              select
              label="Component"
              value={componentFilter}
              onChange={handleComponentChange}
              size="small"
            >
              <MenuItem value="">
                <em>All Components</em>
              </MenuItem>
              {components.map((component) => (
                <MenuItem key={component} value={component}>
                  {component}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 auto' }, minWidth: { md: 0 } }}>
            <TextField
              fullWidth
              select
              label="Sub Component"
              value={subComponentFilter}
              onChange={handleSubComponentChange}
              size="small"
              disabled={!componentFilter}
            >
              <MenuItem value="">
                <em>All Sub Components</em>
              </MenuItem>
              {subComponents.map((subComponent) => (
                <MenuItem key={subComponent} value={subComponent}>
                  {subComponent}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 auto' }, minWidth: { md: 0 } }}>
            <TextField
              fullWidth
              select
              label="Registered"
              value={registeredFilter}
              onChange={handleRegisteredChange}
              size="small"
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              <MenuItem value="true">Registered</MenuItem>
              <MenuItem value="false">Not Registered</MenuItem>
            </TextField>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 auto' }, minWidth: { md: 0 } }}>
            <TextField
              fullWidth
              select
              label="Suite"
              value={suiteFilter}
              onChange={handleSuiteChange}
              size="small"
            >
              <MenuItem value="">
                <em>All Suites</em>
              </MenuItem>
              {suites.map((suite) => (
                <MenuItem key={suite} value={suite}>
                  {suite}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
      </Paper>

      {/* Tests Table - data (title, component, automation status, suite) is always from the tests collection */}
      <Paper elevation={2}>
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Test ID, title, automation status, component, and suite are from the ADO tests collection.
          </Typography>
        </Box>
        <TableContainer>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : sortedTests.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No tests found matching the filters.
              </Typography>
            </Box>
          ) : (
            <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', py: 1 }} align="center">
                    <TableSortLabel
                      active={orderBy === 'isRegistered'}
                      direction={orderBy === 'isRegistered' ? order : 'asc'}
                      onClick={() => handleRequestSort('isRegistered')}
                    >
                      Registered
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1 }}>
                    <TableSortLabel
                      active={orderBy === 'id'}
                      direction={orderBy === 'id' ? order : 'asc'}
                      onClick={() => handleRequestSort('id')}
                    >
                      Test ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1 }}>
                    <TableSortLabel
                      active={orderBy === 'title'}
                      direction={orderBy === 'title' ? order : 'asc'}
                      onClick={() => handleRequestSort('title')}
                    >
                      Test Title
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1 }}>
                    <TableSortLabel
                      active={orderBy === 'automationStatus'}
                      direction={orderBy === 'automationStatus' ? order : 'asc'}
                      onClick={() => handleRequestSort('automationStatus')}
                    >
                      Automation Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1 }}>
                    <TableSortLabel
                      active={orderBy === 'component'}
                      direction={orderBy === 'component' ? order : 'asc'}
                      onClick={() => handleRequestSort('component')}
                    >
                      Component
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1 }}>
                    <TableSortLabel
                      active={orderBy === 'subComponent'}
                      direction={orderBy === 'subComponent' ? order : 'asc'}
                      onClick={() => handleRequestSort('subComponent')}
                    >
                      Sub Component
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 1 }}>
                    <TableSortLabel
                      active={orderBy === 'executedOn'}
                      direction={orderBy === 'executedOn' ? order : 'asc'}
                      onClick={() => handleRequestSort('executedOn')}
                    >
                      Executed on
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>
                    <TableSortLabel
                      active={orderBy === 'suite'}
                      direction={orderBy === 'suite' ? order : 'asc'}
                      onClick={() => handleRequestSort('suite')}
                    >
                      Suite
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTests.map((test) => (
                  <TableRow key={test.id} hover sx={{ '& td': { py: 0.5 } }}>
                    <TableCell align="center" sx={{ py: 0.5 }}>
                      {test.isRegistered ? (
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>{test.id}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {test.title}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Chip
                        label={test.automationStatus || 'N/A'}
                        size="small"
                        sx={{ height: '20px', fontSize: '0.7rem' }}
                        color={
                          test.automationStatus === 'Automated-UI' ||
                          test.automationStatus === 'Automated-API' ||
                          test.automationStatus === 'EPAM-Automated'
                            ? 'success'
                            : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>{test.component || '-'}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{test.subComponent || '-'}</TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      {test.executedOn ? (
                        <Box>
                          <Typography variant="body2" component="div" sx={{ fontSize: '0.75rem', lineHeight: 1.2 }}>
                            {new Date(test.executedOn).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </Typography>
                          <Typography variant="body2" component="div" color="text.secondary" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                            {new Date(test.executedOn).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true
                            })}
                          </Typography>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.5 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        {test.suite || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {sortedTests.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, borderTop: 1, borderColor: 'divider' }}>
                <Stack spacing={2} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Showing {startIndex + 1} to {Math.min(endIndex, sortedTests.length)} of {sortedTests.length} tests
                    {totalPages > 1 && ` (Page ${page} of ${totalPages})`}
                  </Typography>
                  {totalPages > 1 && (
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton
                      showLastButton
                      size="large"
                    />
                  )}
                </Stack>
              </Box>
            )}
            </>
          )}
        </TableContainer>
      </Paper>

      {/* Discrepancy Tests Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{getModalTitle()}</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {modalLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : modalTests.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No tests found for this discrepancy type.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Test Definition ID</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    {modalType === 'deleted' && <TableCell><strong>Matched Test ID</strong></TableCell>}
                    {modalType === 'other' && <TableCell><strong>Duplicate Count</strong></TableCell>}
                    <TableCell><strong>Test Path</strong></TableCell>
                    <TableCell><strong>Group ID</strong></TableCell>
                    <TableCell><strong>Type</strong></TableCell>
                    <TableCell><strong>Synced At</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modalTests.map((test, index) => (
                    <TableRow key={test.testDefinitionId || index} hover>
                      <TableCell>{test.testDefinitionId || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {test.name || '-'}
                        </Typography>
                      </TableCell>
                      {modalType === 'deleted' && (
                        <TableCell>{test.matchedTestId || '-'}</TableCell>
                      )}
                      {modalType === 'other' && (
                        <TableCell>{test.duplicateCount || '-'}</TableCell>
                      )}
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {test.testPath || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>{test.groupId || '-'}</TableCell>
                      <TableCell>{test.type || '-'}</TableCell>
                      <TableCell>
                        {test.syncedAt ? new Date(test.syncedAt).toLocaleString() : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Total: {modalTests.length} tests
          </Typography>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Not in Drill4J Modal */}
      <Dialog
        open={notInDrill4JModalOpen}
        onClose={handleCloseNotInDrill4JModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Tests Not in Drill4J ({notInDrill4JData?.total || 0} tests)
            </Typography>
            <IconButton onClick={handleCloseNotInDrill4JModal} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {notInDrill4JLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : !notInDrill4JData ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No data available.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Tabs value={notInDrill4JTab} onChange={(e, newValue) => setNotInDrill4JTab(newValue)} sx={{ mb: 2 }}>
                <Tab label={`By Test Suite (${notInDrill4JData.byTestSuite?.length || 0} suites)`} />
                <Tab label={`By Automation Status (${notInDrill4JData.byAutomationStatus?.length || 0} statuses)`} />
              </Tabs>

              {notInDrill4JTab === 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Test Suite</strong></TableCell>
                        <TableCell align="right"><strong>Count</strong></TableCell>
                        <TableCell><strong>Test IDs</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {notInDrill4JData.byTestSuite?.map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell><strong>{item.suite || 'Unknown'}</strong></TableCell>
                          <TableCell align="right"><strong>{item.count}</strong></TableCell>
                          <TableCell>
                            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                              {item.tests.map((test, idx) => (
                                <Typography key={idx} variant="body2" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                                  {test.id} - {test.title.substring(0, 60)}{test.title.length > 60 ? '...' : ''}
                                </Typography>
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {notInDrill4JTab === 1 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Automation Status</strong></TableCell>
                        <TableCell align="right"><strong>Count</strong></TableCell>
                        <TableCell><strong>Test IDs</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {notInDrill4JData.byAutomationStatus?.map((item, index) => (
                        <TableRow key={index} hover>
                          <TableCell><strong>{item.status || 'Unknown'}</strong></TableCell>
                          <TableCell align="right"><strong>{item.count}</strong></TableCell>
                          <TableCell>
                            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                              {item.tests.map((test, idx) => (
                                <Typography key={idx} variant="body2" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                                  {test.id} - {test.title.substring(0, 60)}{test.title.length > 60 ? '...' : ''}
                                </Typography>
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            Total: {notInDrill4JData?.total || 0} tests
          </Typography>
          <Button onClick={handleCloseNotInDrill4JModal}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Drill4JTestsTab;

